import React, {createContext, Dispatch, SetStateAction, useContext, useEffect, useState, useMemo, useRef} from 'react'
import SearchService from './SearchService';
import {
  IHandleQuickSearchResult,
  ISearchProviderConfig,
  IQuickSearchResultTypo,
  ISearchAndFilterProduct,
  IQuickSearchResultCollection,
  IQuickSearchResultSuggestion,
  IQuickSearchResultContent,
  ISearchFilter,
  ISearchFilterOption,
  IQuickSearchResultPage,
  IQuickSearchResultArticle,
  IQuickSearchResultPopularSearch,
  IQuickSearchResultTrendingProduct,
  ISearchFilterRangeOption,
  IActiveCollection, IGetProductsResult,
  IGetCmsContentResult,
  ISwatch,
  ISearchFilterStandard,
  ISearchFilterRange,
  ISearchSort
} from './types';
import {useDebouncedCallback} from 'use-debounce';
import updateLocationQuery from './helpers/updateLocationQuery';
import getCurrentActiveFilters from './helpers/getCurrentActiveFilters';

import FILTER_SEARCH from './consts';

import getRoute from '../../../scripts/utils/getRoute';
import axios from 'axios';
import { getClusterId } from './services/Voyado/helpers/voyadoHelpers';
import { scrollTo } from '../../../scripts/global/scrollTo.js';


export interface IFFetchContext {
  children: any,
  config: ISearchProviderConfig,
  activeCollection: IActiveCollection
}

interface ContextValue {
  activeCollection: IActiveCollection,
  searchTerm: string,
  quickSearchTerm: string,
  setQuickSearchTerm: Dispatch<SetStateAction<string>>,
  sortKey: string,
  setSortKey: Dispatch<SetStateAction<string>>,
  pageIndex: number,
  isSearching: boolean,
  quickSearchResult: IHandleQuickSearchResult,
  quickSearchResultsTypos: IQuickSearchResultTypo[],
  quickSearchResultsProducts: ISearchAndFilterProduct[],
  quickSearchResultsCollections: IQuickSearchResultCollection[],
  quickSearchResultsPages: IQuickSearchResultPage[],
  quickSearchResultsArticles: IQuickSearchResultArticle[],
  quickSearchResultsPopularSearches: IQuickSearchResultPopularSearch[],
  quickSearchResultsTrendingProducts: IQuickSearchResultTrendingProduct[],
  quickSearchResultsSuggestions: IQuickSearchResultSuggestion[],
  quickSearchResultsContent: IQuickSearchResultContent[],
  searchResultsArticles: IQuickSearchResultArticle[],
  searchResultsPages: IQuickSearchResultPage[],
  searchFilters: ISearchFilter[],
  filtersLoading: boolean,
  newPageLoading: boolean,
  productSearchResult: IGetProductsResult,
  autoCorrectedQuery: string,
  autoCorrectOriginalTotalHits: number,
  didYouMean: string[],
  productSearchResultsState: IGetProductsResult[],
  cmsSearchResults: IGetCmsContentResult,
  filterSwatches: ISwatch[],
  pendingProductCount: number,
  activeTab: string,
  setActiveTab: Dispatch<SetStateAction<string>>,
  inputEventHandlers: {
    handleSearchInputChange: (any),
    handleSearchInputFocus: (any),
    handleSearchInputKeyPress: (any),
    handleSearchSuggestionSelected: (any),
    handleUpdateRangeOption: (filterIndex: number, optionIndex: number, selectedMinimum: number, selectedMaximum: number) => void,
    handleToggleMultiOption: (filterIndex: number, optionIndex: number, force?: boolean) => void,
    handleToggleSingleOption: (filterIndex: number, optionIndex: number, value: boolean) => void,
    handleUpdatePageIndex: (pageIndex: number, triggerFiltering: boolean, silent: boolean) => void,
    handleSortByChange: (any),
    handleClearSpecificFilter: (filterIndex: number) => void,
    handleClearAllFilters: () => void,
    handleToggleFilterOpen: (filterIndex: number, value?: boolean) => void
    handleApply: () => void
  },
  config: ISearchProviderConfig,
  eventHandlers,
  outputSortElement: ISearchSort[],
  filtersChanged: boolean,
  setFiltersChanged: React.Dispatch<React.SetStateAction<boolean>>,
  hasActiveFilters: boolean,
  markFiltersChanged: () => void,
}

export const FILTER_QUERY_PARAM_PREFIX = 'filter_'
// Voyado uses a pipe
export const FILTER_QUERY_PARAM_SEPARATOR = getClusterId() ? '|' : ','

export const SORT_TYPE_VALS = Object.values(FILTER_SEARCH.SORT_TYPES)

export const SearchContext: React.Context<ContextValue> = createContext(null)

export const SearchProvider: React.FC<IFFetchContext> = ({ children, config, activeCollection }) => {

  // control
  const [isSearching, setIsSearching] = useState<boolean>(false)
  // input
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [quickSearchTerm, setQuickSearchTerm] = useState<string>('')
  const [pageIndex, setPageIndex] = useState<number>(null)

  // data
  const [quickSearchResult, setQuickSearchResult] = useState<IHandleQuickSearchResult>(null)
  const [quickSearchResultsTypos, setQuickSearchResultsTypos] = useState<IQuickSearchResultTypo[]>(null)
  const [quickSearchResultsProducts, setQuickSearchResultsProducts] = useState<ISearchAndFilterProduct[]>(null)
  const [quickSearchResultsCollections, setQuickSearchResultsCollections] = useState<IQuickSearchResultCollection[]>(null)
  const [quickSearchResultsSuggestions, setQuickSearchResultsSuggestions] = useState<IQuickSearchResultSuggestion[]>(null)
  const [quickSearchResultsContent, setQuickSearchResultsContent] = useState<IQuickSearchResultContent[]>(null)
  const [quickSearchResultsPages, setQuickSearchResultsPages] = useState<IQuickSearchResultPage[]>(null)
  const [quickSearchResultsArticles, setQuickSearchResultsArticles] = useState<IQuickSearchResultArticle[]>(null)
  const [quickSearchResultsPopularSearches, setQuickSearchResultsPopularSearches] = useState<IQuickSearchResultPopularSearch[]>(null)
  const [quickSearchResultsTrendingProducts, setQuickSearchResultsTrendingProducts] = useState<IQuickSearchResultTrendingProduct[]>(null)
  const [searchResultsArticles, setSearchResultsArticles] = useState<IQuickSearchResultArticle[]>(null)
  const [searchResultsPages, setSearchResultsPages] = useState<IQuickSearchResultPage[]>(null)

  const [filtersToUpdate, setFiltersToUpdate] = useState<{
    filterIndex: number,
    optionIndex: number,
  }[]>([])


  const [searchFilters, setSearchFilters] = useState<ISearchFilter[]>(null)
  const [isFetchingFilters, setIsFetchingFilters] = useState(false)

  const [sortKey, setSortKey] = useState<string>(null)

  const [productSearchResult, setProductSearchResult] = useState<IGetProductsResult>(null)
  const [productSearchResultsState, setProductResultsState] = useState<IGetProductsResult[]>([])
  const [autoCorrectedQuery, setAutoCorrectedQuery] = useState<string>(null)
  const [autoCorrectOriginalTotalHits, setAutoCorrectOriginalTotalHits] = useState<number>(null)
  const [didYouMean, setDidYouMean] = useState<string[]>(null)

  // Use ref to store latest autoCorrectedQuery so debounced callbacks can access current value
  const autoCorrectedQueryRef = useRef<string>(null)
  const previousContentSearchTermRef = useRef<string>(null)

  const [cmsSearchResults, setCmsSearchResults] = useState<IGetCmsContentResult>(null)

  const [filtersLoading, setFiltersLoading] = useState<boolean>(false)

  const [newPageLoading, setNewPageLoading] = useState<boolean>(false)

  const [filterSwatches, setFilterSwatches] = useState<ISwatch[]>([])

  const [pendingProductCount, setPendingProductCount] = useState<null|number>(null)

  const [filtersChanged, setFiltersChanged] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('products');
  const isFirstRender = React.useRef(true);
  const hasMountedRef = React.useRef(false);

  const markFiltersChanged = () => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setFiltersChanged(true);
  };

  const hasActiveFilters = useMemo(() => {
    if (!searchFilters) return false;
    return searchFilters.some(filter => {
      if (filter.type === 'RANGE') {
        const option = filter.options[0];
        return option.selectedMinimum > option.minimum || option.selectedMaximum < option.maximum;
      } else {
        return filter.options.some(option => option.selected);
      }
    });
  }, [searchFilters]);

  // SessionStorage cache helpers
  const generateCacheKey = (): string => {
    const params = new URLSearchParams(window.location.search);
    const collection = activeCollection?.handle || 'all';
    const term = params.get('q') || searchTerm || '';
    const sort = params.get('sort') || sortKey || '';

    // Include all filter params in cache key
    const filterParams = Array.from(params.entries())
      .filter(([key]) => key.startsWith(FILTER_QUERY_PARAM_PREFIX))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('&');

    return `voyado_search_cache_${collection}_${term}_${sort}_${filterParams}`;
  };

  const saveToCache = (data: IGetProductsResult[], currentPage: number): void => {
    try {
      const cacheKey = generateCacheKey();

      // Convert array to object with explicit page numbers to preserve indices
      const pageMap: Record<number, IGetProductsResult> = {};
      data.forEach((pageData, index) => {
        if (pageData) {
          pageMap[index] = pageData;
        }
      });

      const cacheData = {
        pageMap, // Use object instead of array
        timestamp: Date.now(),
        pageIndex: currentPage,
        scrollPosition: window.scrollY
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.info(`[Search] Cached ${Object.keys(pageMap).length} pages up to page ${currentPage}`);
    } catch (error) {
      console.warn('[Search] Failed to save to sessionStorage:', error);
    }
  };

  const loadFromCache = (): { data: IGetProductsResult[], pageIndex: number, scrollPosition: number } | null => {
    try {
      const cacheKey = generateCacheKey();
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      const age = Date.now() - parsed.timestamp;
      const maxAge = 5 * 60 * 1000; // 5 minutes

      if (age > maxAge) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }

      // Handle both old array format and new pageMap format
      let restoredArray: IGetProductsResult[] = [];

      if (parsed.pageMap) {
        // New format: convert pageMap back to array with proper indices
        const pageNumbers = Object.keys(parsed.pageMap).map(Number).sort((a, b) => a - b);
        const maxPage = Math.max(...pageNumbers);

        // Create array with proper size and populate indices
        restoredArray = new Array(maxPage + 1);
        pageNumbers.forEach(pageNum => {
          restoredArray[pageNum] = parsed.pageMap[pageNum];
        });

        console.info(`[Search] Restored pages from cache: [${pageNumbers.join(', ')}]`);
      } else if (parsed.data) {
        // Old format: direct array (for backwards compatibility)
        restoredArray = parsed.data;
      }

      return {
        data: restoredArray,
        pageIndex: parsed.pageIndex,
        scrollPosition: parsed.scrollPosition
      };
    } catch (error) {
      console.warn('[Search] Failed to load from sessionStorage:', error);
      return null;
    }
  };

  const clearCache = (): void => {
    try {
      // Clear all Voyado cache keys
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('voyado_search_cache_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('[Search] Failed to clear cache:', error);
    }
  };

  const updateFilters = (newFilters: ISearchFilter[], activate?: boolean): void => {

    const isFiltered = window.location.search.includes(FILTER_QUERY_PARAM_PREFIX)

    const updatedFilters = searchFilters.map(filter => {

      const newFilter = newFilters.find(({ identifier }) => identifier === filter.identifier)

      if (filter.type === FILTER_SEARCH.FILTER_TYPES.RANGE) {

        const rangeFilter = filter as ISearchFilterRange
        if (!activate) return rangeFilter
        const option = rangeFilter.options[0]

        return {
          ...filter,
          options: [
            {
              ...option,
              activeMaximum: option.selectedMaximum,
              activeMinimum: option.selectedMinimum

            }
          ]
        } as ISearchFilterRange
      }

      const oldOptions = filter.options as ISearchFilterOption[] // as not range type
      const newOptions  = newFilter ? newFilter.options as ISearchFilterOption[] : []

      return {
        ...filter,
        options: oldOptions.map((option) => {
          const newOption = newOptions.find(({ identifier }) => identifier && identifier === option.identifier)
          return {
            ...option,
            ...activate ? {
              active: option.selected
            } : {},
            // This needs to be set to 0 once we've filtered, but option.records before
            records: newOption ? newOption.records : (isFiltered ? 0 : option.records)
          }
        })
      } as ISearchFilterStandard
    })

    setSearchFilters([...updatedFilters])
  }

  const wipeLiquidProducts = (): void => {
    const liquidProducts = document.querySelector('[data-liquid-products]')
    if (liquidProducts) {
      liquidProducts.innerHTML = '';
    }
  }

  const refreshFiltersOnly = () => {
    // Get effective search term at call time to ensure we have latest state
    // Use ref to get the most current value for debounced callbacks
    const isSearchPage = window['theme']?.template === 'search';
    const currentAutoCorrected = autoCorrectedQueryRef.current;
    const effectiveTerm = (isSearchPage && currentAutoCorrected && currentAutoCorrected !== searchTerm)
      ? currentAutoCorrected
      : searchTerm;

    SearchService.methods.getProductResults(
      searchFilters,
      activeCollection,
      { term: effectiveTerm, pageIndex: pageIndex, sortKey: sortKey },
      config
    ).then((response) => {
      if (response.filters) {
        updateFilters(response.filters, false)
      }
      setPendingProductCount(response.total)
    })
  }

  const setResultsClass = (): void => {
    const resultsEl = document.querySelector('.search-results')
    if (resultsEl) {
      resultsEl.classList.add('search__results--loaded');
    }
  }

  const executeSearch = useDebouncedCallback((term) => {
    // result all search results if the search field has been emptied
    // if (term.length === 0) {
    //   setIsSearching(false)
    //   setQuickSearchResult(null)
    //   setQuickSearchResultsTypos(null)
    //   setQuickSearchResultsProducts(null)
    //   setQuickSearchResultsCollections(null)
    //   setQuickSearchResultsSuggestions(null)
    //   setQuickSearchResultsContent(null)
    //   setQuickSearchResultsPages(null)
    //   setQuickSearchResultsArticles(null)
    //   setResultsClass()
    // } else {
    SearchService.methods.handleSearch(term, config).then((response) => {
      setIsSearching(false)
      setQuickSearchResult(response)
      setQuickSearchResultsTypos(response.typos)
      setQuickSearchResultsProducts(response.products)
      setQuickSearchResultsCollections(response.collections)
      setQuickSearchResultsSuggestions(response.suggestions)
      setQuickSearchResultsPages(response.pages)
      setQuickSearchResultsArticles(response.articles)
      setQuickSearchResultsPopularSearches(response.popularSearches)
      setQuickSearchResultsTrendingProducts(response.trendingProducts)
      setQuickSearchResultsContent(null) // @todo
      setResultsClass()


    })
    // }
  }, 1000)

  const outputProductData = (productData: IGetProductsResult, checkPageIndex: number) => {

    const emptyResponse: IGetProductsResult = {
      records: 0,
      total: 0,
      raw: null,
      products: [],
      filters: []
    }

    const state = productSearchResultsState
    if (checkPageIndex) {
      state[checkPageIndex] = productData

    }

    if (config.rLoadMoreMode !== 'paginate' && checkPageIndex) {
      // infinite scroll set all productResult data (state[0] is undefined, hence .filter)
      const allData = state.filter(def => def).reduce((all, thisData) => {
        return {
          records: all.records + thisData.records,
          total: thisData.total,
          products: [...all.products, ...thisData.products],
          filters: thisData.filters,
          raw: [...(all.raw ?? []),
          //if thisData.raw is an array then spread (I believe this is used for boost)
          //otherwise go and get thisData.raw.queryResults[0].records and spread that (this is used for Klevu v2)
            ...(Array.isArray(thisData.raw) ?
              thisData.raw : thisData.raw?.queryResults?.[0]?.records?.length > 0 ?
                thisData.raw.queryResults[0].records : [])],
          sort: thisData.sort ?? [],
          isSecondaryList: thisData.isSecondaryList ?? all.isSecondaryList
        }
      }, emptyResponse)
      setProductSearchResult(allData)
    } else {
      // pagination, only want specific page of products
      setProductSearchResult(productData)
    }
    setPendingProductCount(productData.total)

    // Save to cache after updating state
    if (checkPageIndex) {
      saveToCache([...state], checkPageIndex);
    }
  }

  const outputSortElement = (sortData: IGetProductsResult) => {
    return sortData?.sort ? sortData.sort : [];
  }

  const executeFilteredSearch = useDebouncedCallback((forceIndex = null) => {
    const checkPageIndex = forceIndex ?? pageIndex
    if (checkPageIndex && productSearchResultsState[checkPageIndex]) {
      wipeLiquidProducts();
      outputProductData(productSearchResultsState[checkPageIndex], checkPageIndex)
      setFiltersLoading(false)
      setNewPageLoading(false)

    } else {
      // Get effective search term at call time to ensure we have latest state
      // Use ref to get the most current value for debounced callbacks
      const isSearchPage = window['theme']?.template === 'search';
      const currentAutoCorrected = autoCorrectedQueryRef.current;
      const effectiveSearchTerm = (isSearchPage && currentAutoCorrected && currentAutoCorrected !== searchTerm)
        ? currentAutoCorrected
        : searchTerm;

      SearchService.methods.getProductResults(
        searchFilters,
        activeCollection,
        { term: effectiveSearchTerm, pageIndex: checkPageIndex, sortKey: sortKey },
        config
      ).then((response) => {
        console.log('🔥 executeFilteredSearch autocorrect debug:', {
          responseAutoCorrectedQuery: response.autoCorrectedQuery,
          currentAutoCorrected: currentAutoCorrected,
          searchTerm: searchTerm,
          isSearchPage: isSearchPage
        });
        wipeLiquidProducts();
        outputProductData(response, checkPageIndex)
        outputSortElement(response)

        const isUsingCorrectedQuery = (isSearchPage && currentAutoCorrected && currentAutoCorrected !== searchTerm);
        if (isUsingCorrectedQuery && !response.autoCorrectedQuery) {
          // Keep existing autoCorrectedQuery state - don't overwrite with null
          console.log('🔥 Keeping existing autocorrect (using corrected query but response has none)');
        } else {
          // Update with response value (either new correction or null if no correction needed)
          console.log('🔥 Setting autocorrect to:', response.autoCorrectedQuery);
          setAutoCorrectedQuery(response.autoCorrectedQuery)
          autoCorrectedQueryRef.current = response.autoCorrectedQuery; // Update ref immediately
        }

        setAutoCorrectOriginalTotalHits(response.autoCorrectOriginalTotalHits)
        setDidYouMean(response.didYouMean)

        if (response.filters) {
          updateFilters(response.filters, true)
          setFiltersLoading(false)
        }
        if (checkPageIndex) {
          const state = productSearchResultsState
          setProductResultsState([...state])
          setFiltersLoading(false)
        }
        setNewPageLoading(false)

        const termForContent = (isSearchPage && response.autoCorrectedQuery && response.autoCorrectedQuery !== searchTerm)
          ? response.autoCorrectedQuery
          : searchTerm;

        const hasSearchTermChanged = termForContent !== previousContentSearchTermRef.current;

        if (termForContent && hasSearchTermChanged) {
          previousContentSearchTermRef.current = termForContent;

          SearchService.methods.handleSearch(termForContent, config).then((committedContentResponse) => {
            setSearchResultsArticles(committedContentResponse.articles || [])
            setSearchResultsPages(committedContentResponse.pages || [])
          }).catch((error) => {
            console.error('Error fetching committed content during filtered search:', error)
            // Clear content on error
            setSearchResultsArticles([])
            setSearchResultsPages([])
          })
        } else if (!termForContent) {
          // Clear content if no search term
          previousContentSearchTermRef.current = null;
          setSearchResultsArticles([])
          setSearchResultsPages([])
          setAutoCorrectedQuery(null)
          autoCorrectedQueryRef.current = null; // Clear ref
          setAutoCorrectOriginalTotalHits(null)
          setDidYouMean(null)
        }
      }).catch((error) => {
        console.error('[Search] Error fetching product results:', error);
        // Always reset loading states on error
        setFiltersLoading(false);
        setNewPageLoading(false);
      })
    }
  }, 1000)

  const setLoadingAndExecuteFilteredSearch = () => {

    const liquidProducts = document.querySelector('[data-liquid-products]')

    if (liquidProducts) {
      liquidProducts.classList.add('is-loading')
    }

    setFiltersLoading(true)
    setNewPageLoading(true)

    executeFilteredSearch.callback()
  }

  const updatePageIndex = (pageIndex: number, triggerFiltering: boolean, silent: boolean, historyMethod: 'pushState' | 'replaceState' = 'pushState') => {
    if (window['theme'].template === 'search' || window['theme'].template === 'collection') {
      let forceSilentIndex = null
      setNewPageLoading(true)
      if (!silent) {
        // update state
        setPageIndex(pageIndex)

        // set url param
        updateLocationQuery({
          key: 'page',
          historyMethod,
          value: pageIndex.toString(),
          action: 'set'
        })

        if (config.rLoadMoreMode === 'paginate' && (pageIndex > 1 || forceSilentIndex)) {
          scrollTo('body', { top: 0, behavior: 'smooth' });
        }
      } else {
        // silent update
        forceSilentIndex = pageIndex
      }

      // re-filter
      if (triggerFiltering) {
        executeFilteredSearch.callback(forceSilentIndex);
      } else {
        setNewPageLoading(false)
      }
    }
  }

  const eventHandlers = {
    filterUpdated: (params: {
      filterIndex: number,
      optionIndex: number,
    }) => {
      // get the filter
      const filter = searchFilters[params.filterIndex]
      if (['SINGLE_OPTION', 'MULTI_OPTION'].includes(filter.type)) {

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const options: ISearchFilterOption[] = filter.options.map((option: ISearchFilterOption) => {
          // Shopify needs to use the option value rather than identifier
          return option.selected ? option.value ? option.value : option.identifier : false
        }).filter(option => option)


        if (options.length) {
          updateLocationQuery({
            key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
            value: options.join(FILTER_QUERY_PARAM_SEPARATOR),
            action: 'set'
          })
        } else {
          if (options.length > 0) {
            updateLocationQuery({
              key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
              value: options.join(FILTER_QUERY_PARAM_SEPARATOR),
              action: 'set'
            })
          } else {
            updateLocationQuery({
              key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
              action: 'delete'
            })
          }
        }
      } else if (['RANGE'].includes(filter.type)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const option: ISearchFilterRangeOption = filter.options[params.optionIndex]
        const isActive = (option.selectedMinimum > option.minimum || option.selectedMaximum < option.maximum)
        if (isActive) {
          updateLocationQuery({
            key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
            value: `${option.selectedMinimum}:${option.selectedMaximum}`,
            action: 'set'
          })
        } else {
          updateLocationQuery({
            key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
            action: 'delete'
          })
        }
      } else {
        throw 'Unhandled filter type'
      }

      updatePageIndex(1, false, false, 'replaceState')
      // invalidate paginated state and clear cache
      setProductResultsState([])
      clearCache()
    },

    sortByUpdated: (
      params: {
        sortKey: string
      }
    ) => {

      if (!SORT_TYPE_VALS.includes(params.sortKey)) {
        console.info('Incorrect sort type')
      }
      if (params.sortKey && params.sortKey !== 'null') {
        updateLocationQuery({
          key: 'sort',
          value: params.sortKey,
          action: 'set'
        })
        setSortKey(params.sortKey)
      }
      updatePageIndex(1, false, false, 'replaceState')
      // invalidate paginated state and clear cache
      setProductResultsState([])
      clearCache()
    }
  }



  /**
   * Input event handlers
   */
  const inputEventHandlers = {

    handleSearchInputChange: (e) => {
      const query = e.target.value

      // If empty: clear non-suggestion results, then trigger empty autocomplete to populate suggestions (recent searches)
      if (!query || !query.length) {
        setQuickSearchTerm('')
        executeSearch.cancel()
        setIsSearching(true)
        setQuickSearchResult(null)
        setQuickSearchResultsTypos(null)
        setQuickSearchResultsProducts(null)
        setQuickSearchResultsCollections(null)
        setQuickSearchResultsContent(null)
        setQuickSearchResultsPages(null)
        setQuickSearchResultsArticles(null)
        // keep suggestions managed by executeSearch('')
        executeSearch.callback('')
        return
      }

      setQuickSearchTerm(query)
      setIsSearching(true)

      executeSearch.callback(query)
    },

    handleSearchInputFocus: () => {
      // When search input is focused but empty, show trending products and popular searches
      if (!quickSearchTerm || !quickSearchTerm.length) {
        setIsSearching(true)
        executeSearch.callback('') // Call with empty string to get trending/popular data
      }
    },

    handleSearchInputKeyPress: (e) => {
      if (e.keyCode === 13) {
        window.location.href = `${getRoute()}search?q=${quickSearchTerm}`
      }
    },

    handleSearchSuggestionSelected: (index) => {

      // remove any HTML which may have been added to the suggestion result
      const suggestion = quickSearchResultsSuggestions[index].value.replace(/(<([^>]+)>)/gi, '')
      setIsSearching(true)
      setSearchTerm(suggestion)
      executeSearch.callback(suggestion)
    },

    handleUpdateRangeOption: (filterIndex: number, optionIndex: number, selectedMinimum: number, selectedMaximum: number) => {

      const updatedOption = [...searchFilters][filterIndex].options[optionIndex] as ISearchFilterRangeOption
      updatedOption.selectedMinimum = selectedMinimum;
      updatedOption.selectedMaximum = selectedMaximum;

      const updatedSearchFilters = [...searchFilters]

      updatedSearchFilters[filterIndex].options[optionIndex] = updatedOption

      setSearchFilters([...updatedSearchFilters])

      setFiltersToUpdate([...filtersToUpdate, {
        filterIndex: filterIndex,
        optionIndex: optionIndex,
      }])

      refreshFiltersOnly()

    },

    handleToggleMultiOption: (filterIndex: number, optionIndex: number, forceValue?: boolean) => {
      const updatedOption = [...searchFilters][filterIndex].options[optionIndex] as ISearchFilterOption

      updatedOption.selected = typeof forceValue === 'undefined' ? !updatedOption.selected : forceValue

      const updatedSearchFilters = [...searchFilters]

      updatedSearchFilters[filterIndex].options[optionIndex] = updatedOption

      setSearchFilters([...updatedSearchFilters])

      setFiltersToUpdate([...filtersToUpdate, {
        filterIndex: filterIndex,
        optionIndex: optionIndex,
      }])

      refreshFiltersOnly()
    },

    handleToggleSingleOption: (filterIndex: number, optionIndex: number, value: boolean) => {

      const updatedFilterOptions = [...searchFilters][filterIndex].options as ISearchFilterOption[];


      for (let i = 0; i < updatedFilterOptions.length; i++) {
        updatedFilterOptions[i].selected = false
      }

      updatedFilterOptions[optionIndex].selected = value

      const updatedSearchFilters = [...searchFilters]
      updatedSearchFilters[filterIndex].options = updatedFilterOptions;

      setSearchFilters([...updatedSearchFilters])

      setFiltersToUpdate([...filtersToUpdate, {
        filterIndex: filterIndex,
        optionIndex: optionIndex,
      }])

      refreshFiltersOnly()

    },

    handleUpdatePageIndex: (pageIndex: number, triggerFiltering: boolean, silent: boolean) => {
      updatePageIndex(pageIndex, triggerFiltering, silent, 'pushState')
    },

    handleSortByChange: (val: string) => {
      eventHandlers.sortByUpdated({
        sortKey: val
      })
      setLoadingAndExecuteFilteredSearch()
    },

    handleClearSpecificFilter: (filterIndex: number) => {
      const updatedFilters = [...searchFilters]
      const filterToClear = updatedFilters[filterIndex]
      if (filterToClear.type === 'RANGE') {
        const option = filterToClear.options[0] as ISearchFilterRangeOption
        option.selectedMaximum = Math.ceil(option.maximum);
        option.selectedMinimum = Math.floor(option.minimum);

      } else {
        const indices = []
        const ticked = (filterToClear.options as ISearchFilterOption[]).map(({selected}, i) => ({selected, i}))
        if (ticked.length) {
          for (const selected of ticked) {
            if (!selected.selected) continue;
            indices.push({int: selected.i})
          }
        }

        if (!indices.length) {
          return null
        }
        indices.forEach(({ int }) => inputEventHandlers.handleToggleMultiOption(filterIndex, int))
      }

      setSearchFilters(updatedFilters)
      refreshFiltersOnly()
    },

    handleClearAllFilters: (): void => {
      const updatedFilters: ISearchFilter[] = [...searchFilters];

      for (let i = 0; i < updatedFilters.length; i++) {
        const filter: ISearchFilter = updatedFilters[i];

        if (filter.type === 'RANGE') {
          const option: ISearchFilterRangeOption = filter.options[0];
          option.selectedMaximum = Math.ceil(option.maximum);
          option.selectedMinimum = Math.floor(option.minimum);
          continue;
        }

        for (let j = 0; j < filter.options.length; j++) {
          const option: ISearchFilterRangeOption | ISearchFilterOption = filter.options[j];
          option.selected = false;
        }
      }

      setSearchFilters(updatedFilters);
      refreshFiltersOnly();
    },

    handleToggleFilterOpen: (filterIndex: number, value?: boolean) => {
      const updatedFilters = [...searchFilters].map((filter, index) => {
        if (index !== filterIndex) {
          return {
            ...filter
          }
        } else {
          return {
            ...filter,
            open: typeof value !== 'undefined' ? value : !filter.open
          }
        }
      })
      setSearchFilters(updatedFilters)

    },

    handleApply: () => {

      // invalidate paginated state and clear cache
      setProductResultsState([])
      clearCache()

      setLoadingAndExecuteFilteredSearch()

      // execute filter updated event
      filtersToUpdate.map(({filterIndex, optionIndex }) => {
        eventHandlers.filterUpdated({
          filterIndex: filterIndex,
          optionIndex: optionIndex,
        })
      })

    }

  }


  const getCurrentUrlWithTagSet = (tag: string, val: boolean) => {
    const path = window.location.pathname.split('/').filter(part => part);
    let newPath = `/${ path.join('/')}`
    if (val && path[path.length - 1] !== tag) { // needs to add
      newPath = `/${path.join('/')}/${tag}`
    } else if (!val && path[path.length - 1] === tag) { // needs to remove
      newPath = `/${path.slice(0, -1).join('/')}`
    }
    return `${window.location.origin}${newPath}${window.location.search}${window.location.hash}`;
  }

  const handlePopState = () => {
    const params = new URLSearchParams(window.location.search)
    const pageParam = params.get('page')
    const page = pageParam && !isNaN(parseInt(pageParam)) ? parseInt(pageParam) : 1;
    if (page) {
      updatePageIndex(page, true, false, 'replaceState')
    }
  }

  // Timeout failsafe for stuck loading state
  useEffect(() => {
    if (!newPageLoading) {
      return () => {}; // Return empty cleanup function
    }

    const timeout = setTimeout(() => {
      console.warn('[Search] Page load timeout after 10s, resetting newPageLoading');
      setNewPageLoading(false);
    }, 10000); // 10 second timeout

    return () => {
      clearTimeout(timeout);
    };
  }, [newPageLoading]);

  // Load from cache on mount for back button navigation
  useEffect(() => {
    if (!hasMountedRef.current && (window['theme'].template === 'search' || window['theme'].template === 'collection')) {
      hasMountedRef.current = true;

      const params = new URLSearchParams(window.location.search);
      const currentPage = parseInt(params.get('page') || '1');

      // Only try to restore cache if we're on page 2+
      if (currentPage > 1) {
        const cached = loadFromCache();

        if (cached && cached.data.length > 0) {
          console.info(`[Search] Attempting to restore from cache for page ${currentPage}`);

          // Check which pages we have in cache
          const cachedPages: number[] = [];
          const missingPages: number[] = [];

          for (let i = 1; i <= currentPage; i++) {
            if (cached.data[i] && cached.data[i].products && cached.data[i].products.length > 0) {
              cachedPages.push(i);
            } else {
              missingPages.push(i);
            }
          }

          console.info(`[Search] Cache status - Found: [${cachedPages.join(', ')}], Missing: [${missingPages.join(', ')}]`);

          // Restore the state with cached data
          setProductResultsState(cached.data);
          setPageIndex(cached.pageIndex);

          // Rebuild the combined product result for infinite scroll
          if (config.rLoadMoreMode !== 'paginate') {
            const emptyResponse: IGetProductsResult = {
              records: 0,
              total: 0,
              raw: null,
              products: [],
              filters: [],
              sort: []
            };

            const allData = cached.data.filter(def => def).reduce((all, thisData) => {
              return {
                records: all.records + thisData.records,
                total: thisData.total,
                products: [...all.products, ...thisData.products],
                filters: thisData.filters,
                raw: [...(all.raw ?? []),
                  ...(Array.isArray(thisData.raw) ?
                    thisData.raw : thisData.raw?.queryResults?.[0]?.records?.length > 0 ?
                      thisData.raw.queryResults[0].records : [])],
                sort: thisData.sort ?? []
              }
            }, emptyResponse);

            setProductSearchResult(allData);
          }

          // Restore scroll position after React renders
          requestAnimationFrame(() => {
            window.scrollTo(0, cached.scrollPosition);
            // Clear liquid dummy products
            wipeLiquidProducts();
          });

          // Force fetch any missing pages
          if (missingPages.length > 0) {
            console.warn(`[Search] Force fetching missing pages: [${missingPages.join(', ')}]`);
            // Fetch missing pages sequentially
            missingPages.forEach((pageNum, index) => {
              setTimeout(() => {
                console.info(`[Search] Fetching missing page ${pageNum}`);
                updatePageIndex(pageNum, true, true, 'replaceState');
              }, index * 100); // Stagger requests slightly
            });
          }
        } else {
          console.info('[Search] No valid cache found, will fetch pages normally');
        }
      }
    }
  }, []);

  //on render
  useEffect(() => {
    //check if url includes filtered as getting weird safari bug, SearchFilteredProducts component was kicking in on PLP when back clicked
    if (window.location.pathname.includes(config.searchFilterDummyTag)) {
      window.addEventListener('popstate', () => handlePopState());
    }

    if (window['swatches'] && Object.keys(window['swatches']).length) {
      const swatches = SearchService.methods.getSwatchData(window['swatches']);
      setFilterSwatches(swatches)
    }

    // added in date logic and local storage to aid efficeiency of collection retrieval
    const now = new Date();
    const localCollections = JSON.parse(window.localStorage.getItem('bb-collections'));
    if (!localCollections || (localCollections && localCollections['00-timer'] && now > localCollections['00-timer'])) {
      const storageTimeMinutes = 10;
      // used to populate list of collection urls and their ids for use in collecitonOptionFilter
      axios.get('/collections/all?view=json')
        .then((res) => {
          if (res.data?.collections) {
          // add an additional timer element to the array so we can store for later checking and invalidate after a time
            window['theme']['collections'] = {...{'00-timer': new Date(now.getTime() + storageTimeMinutes*60000)}, ...res.data.collections}
            // This is not pretty but is the easiest fix for now - sorry
            axios.get('/collections/all?page=2&view=json')
              .then((res) => {
                if (res.data?.collections) {
                  window['theme']['collections'] = {...window['theme']['collections'], ...res.data.collections}
                  window.localStorage.setItem('bb-collections', JSON.stringify(window['theme']['collections']))
                // Uncomment the below line to view all collections object
                // console.log(window['theme']['collections'])
                }
              })
              .catch(e => {
                console.error(e);
              });

          }
        })
        .catch(e => {
          console.error(e);
        });
    } else {
      window['theme']['collections'] = localCollections;
    }

  }, [])

  /**
   * Initialise Search Filters
   */
  useEffect(() => {
    // parse query params for search context
    const params = new URLSearchParams(window.location.search)

    let executeSearch = false
    let localSearchTerm = null;
    if (!searchTerm) {
      if (params.get('q')) {
        localSearchTerm = params.get('q')
        setSearchTerm(localSearchTerm)
        executeSearch = true
      }
    }

    if (!pageIndex) {
      if (params.get('page')) setPageIndex(parseInt(params.get('page')))
      else setPageIndex(1)
    }

    if (!sortKey) {
      if (params.get('sort')) {

        setSortKey(params.get('sort'))
        executeSearch = true
      }
    }

    // If you wish to trigger React on PLP initial load
    if (window['theme'].template === 'collection') {
      executeSearch = true;
    }

    if (!cmsSearchResults && params.get('q') && SearchService.methods.getCmsContent) {
      SearchService.methods.getCmsContent(params.get('q'), config).then((response) => {
        setCmsSearchResults(response)
      }).catch((error) => {
        console.error(error)
        // @todo handle error for getting cms content
      })
    }

    if (!searchFilters && !isFetchingFilters && (window['theme'].template === 'search' || window['theme'].template === 'collection')) {
      setIsFetchingFilters(true);
      SearchService.methods.getFilters(activeCollection, localSearchTerm, config).then((response) => {
        // Klevu min & max ranges coming back as strings and breaking range slider
        const rangeFilters = response.filters.find(filter => filter.displayType === 'RANGE')
        if (rangeFilters?.options?.length > 0) {
          const rangeOption = rangeFilters.options[0] as ISearchFilterRangeOption
          if (typeof rangeOption.maximum === 'string') {
            rangeOption.maximum = parseFloat(rangeOption.maximum)
          }
          if (typeof rangeOption.minimum === 'string') {
            rangeOption.minimum = parseFloat(rangeOption.minimum)
          }
        }

        // parse relevant filters from URL
        const selectedFilters = []
        params.forEach((values, key) => {
          if (key.startsWith(FILTER_QUERY_PARAM_PREFIX)) {
            values.split(FILTER_QUERY_PARAM_SEPARATOR).forEach((value) => {
              selectedFilters.push({
                filterKey: key,
                optionKey: value
              })
            })
          }
        })

        // Initialise filter states
        if (selectedFilters.length > 0) {
          selectedFilters.forEach((selectedFilter: {
            filterKey: string,
            optionKey: string
          }) => {
            // find the filter index
            const filterIndex = response.filters.findIndex((filter) => {
              return `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}` === selectedFilter.filterKey
            })

            if (filterIndex === -1) return

            switch (response.filters[filterIndex].type) {
              case 'SINGLE_OPTION':
              case 'MULTI_OPTION': {
                const optionIndex = response.filters[filterIndex].options.findIndex((option) => {
                  return option.value === selectedFilter.optionKey
                })

                if (optionIndex >= 0) {
                  const option = (response.filters[filterIndex] as ISearchFilterStandard).options[optionIndex];
                  option.active = true
                  option.selected = true
                  executeSearch = true
                }
                break;
              }
              case 'RANGE': {
                const option = (response.filters[filterIndex] as ISearchFilterRange).options[0];
                option.selectedMinimum = parseFloat(selectedFilter.optionKey.split(':')[0]);
                option.selectedMaximum = parseFloat(selectedFilter.optionKey.split(':')[1]);
                option.activeMinimum = option.selectedMinimum
                option.activeMaximum = option.selectedMaximum
                executeSearch = true
                break;
              }

              default:
                break;
            }
          })
        }
        setSearchFilters(response.filters)

        // Extract autoCorrect from GetFilters response (GET request has autoCorrect, POST with contentLists doesn't)
        if (response.autoCorrectedQuery) {
          setAutoCorrectedQuery(response.autoCorrectedQuery);
          autoCorrectedQueryRef.current = response.autoCorrectedQuery;
          setAutoCorrectOriginalTotalHits(response.autoCorrectOriginalTotalHits);
        }

        setIsFetchingFilters(false);

        isFirstRender.current = false;

        if (executeSearch) setLoadingAndExecuteFilteredSearch()

      })
    }

    const selectedFilters = searchFilters ? getCurrentActiveFilters(searchFilters) : [];
    const updateUrlWithTag = Boolean(productSearchResult && activeCollection.handle && (selectedFilters.length > 0 || sortKey))

    const url = getCurrentUrlWithTagSet(config.searchFilterDummyTag, updateUrlWithTag)
    if (window.history && window.location.toString() !== url) {
      window.history.replaceState({ ...window.history.state }, '', url)
    }

  }, [productSearchResult, searchTerm, sortKey])

  /**
   * Update document.title based on Voyado search results
   */
  useEffect(() => {
    if (!searchTerm) return;
    if (!productSearchResult) {
      try {
        document.title = `Search: Searching for "${searchTerm}" – ${window['blubolt']?.shopName ?? ''}`.trim();
      } catch (_) { /* noop */ }
      return;
    }
    const total = typeof productSearchResult.total === 'number' ? productSearchResult.total : 0;
    const queryForTitle = (autoCorrectedQuery && autoCorrectedQuery !== searchTerm) ? autoCorrectedQuery : searchTerm;
    try {
      document.title = `Search: ${total} result${total === 1 ? '' : 's'} found for "${queryForTitle}" – ${window['blubolt']?.shopName ?? ''}`.trim();
    } catch (_) { /* noop */ }
  }, [productSearchResult, searchTerm, autoCorrectedQuery])

  return (
    <SearchContext.Provider value={{
      activeCollection,
      searchTerm,
      quickSearchTerm,
      setQuickSearchTerm,
      pageIndex,
      quickSearchResult,
      quickSearchResultsTypos,
      quickSearchResultsSuggestions,
      quickSearchResultsProducts,
      quickSearchResultsCollections,
      quickSearchResultsContent,
      quickSearchResultsPages,
      quickSearchResultsArticles,
      quickSearchResultsPopularSearches,
      quickSearchResultsTrendingProducts,
      searchResultsArticles,
      searchResultsPages,
      filterSwatches,
      searchFilters,
      filtersLoading,
      newPageLoading,
      sortKey,
      setSortKey,
      productSearchResult,
      autoCorrectedQuery,
      autoCorrectOriginalTotalHits,
      didYouMean,
      productSearchResultsState,
      cmsSearchResults,
      isSearching,
      inputEventHandlers,
      config,
      pendingProductCount,
      eventHandlers,
      outputSortElement: outputSortElement(productSearchResult),
      filtersChanged,
      setFiltersChanged,
      hasActiveFilters,
      markFiltersChanged,
      activeTab,
      setActiveTab,
    }}>
      {children}
    </SearchContext.Provider>
  )
}

const useSearch = (): ContextValue => useContext(SearchContext)

export default useSearch
